import React, { useState, useEffect, useRef } from "react";
import { MapPin, Save, X } from "lucide-react";
import { Modal } from "./Modal";

/**
 * Capture end-user details + certificate address in a single modal.
 * Pop-up form for the "End User" checkbox on the Receiving page so the user
 * doesn't have to scroll the long form.
 *
 * The parent owns all the state (so draft autosave keeps working); this modal
 * is a local-buffer-on-edit + commit-on-save UX wrapper.
 */
export function EndUserModal({
  open, onClose, onSave, onCancelTickback,
  customer,
  // Current values from parent
  initial,
  // For contacts dropdown
  contacts = [],
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactId, setContactId] = useState(null);
  const [company, setCompany] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [country, setCountry] = useState("");
  const [postal, setPostal] = useState("");
  const [addrUnknown, setAddrUnknown] = useState(false);
  const [addrUnknownReason, setAddrUnknownReason] = useState("");
  // Tracks whether the user actually edited any field inside the modal.
  // Cancel-with-no-edits should un-tick the parent's End User checkbox even if
  // the modal opened with auto-prefilled values (e.g. from the primary contact).
  const touched = useRef(false);
  const touch = () => { touched.current = true; };

  // Re-hydrate local state every time the modal opens
  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setEmail(initial?.email || "");
    setPhone(initial?.phone || "");
    setContactId(initial?.contactId || null);
    setCompany(initial?.company || "");
    setAddr1(initial?.addr1 || "");
    setAddr2(initial?.addr2 || "");
    setCity(initial?.city || "");
    setProvince(initial?.province || "");
    setCountry(initial?.country || "");
    setPostal(initial?.postal || "");
    setAddrUnknown(!!initial?.addrUnknown);
    setAddrUnknownReason(initial?.addrUnknownReason || "");
    touched.current = false;
  }, [open, initial]);

  const applyContact = (c) => {
    touch();
    setName(c.name || "");
    setEmail(c.email || "");
    setPhone(c.phone || "");
    setContactId(c.id);
  };

  const fillFromCustomer = () => {
    if (!customer) return;
    touch();
    setCompany(customer.customer_name || "");
    setAddr1(customer.address_line_1 || "");
    setAddr2(customer.address_line_2 || "");
    setCity(customer.city || "");
    setProvince(customer.province || "");
    setCountry(customer.country || "");
    setPostal(customer.postal_code || "");
  };

  const save = () => {
    onSave({
      name, email, phone, contactId,
      company, addr1, addr2, city, province, country, postal,
      addrUnknown, addrUnknownReason,
    });
    onClose();
  };

  // Cancel: only "untick" the parent's End User checkbox if the user has not
  // actually edited any field inside the modal. We can't simply check whether
  // the local state contains values — the parent may have auto-prefilled them
  // from the customer's primary contact, in which case the user is bailing out
  // before adding anything of their own.
  const cancel = () => {
    if (!touched.current && onCancelTickback) onCancelTickback();
    onClose();
  };

  if (!open) return null;

  return (
    <Modal open onClose={cancel} icon={MapPin} wide
      title="End user — certificate details"
      subtitle={customer ? `Billing customer: ${customer.customer_name}` : "Pick a customer first to enable contact lookups"}
      footer={
        <>
          <button className="btn ghost" onClick={cancel} data-testid="enduser-modal-cancel">
            <X size={14} /> Cancel
          </button>
          <button className="btn gold" onClick={save} data-testid="enduser-modal-save">
            <Save size={14} /> Save end-user details
          </button>
        </>
      }>
      <p className="t-mut" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
        The paying customer stays as the billing account.
        These details set the <b>name &amp; address printed on the certificate</b>,
        plus a notification contact for this job.
      </p>

      {/* Contact picker / fields */}
      <div className="sec-h" style={{ marginTop: 0 }}>
        <h3 style={{ fontSize: 13 }}>End-user contact</h3>
        {contacts.length > 0 && (
          <span className="tag-soft" style={{ fontSize: 11 }}>
            {contacts.length} contact{contacts.length === 1 ? "" : "s"} on file
          </span>
        )}
      </div>

      {contacts.length > 0 && (
        <div className="card" style={{ marginBottom: 12, maxHeight: 140, overflowY: "auto" }}>
          {contacts.map((c) => (
            <button type="button" key={c.id} className="nav-item"
              style={{ color: "var(--text)", borderRadius: 0, padding: "8px 10px",
                       background: contactId === c.id ? "var(--card-2)" : undefined,
                       width: "100%", textAlign: "left" }}
              onClick={() => applyContact(c)}
              data-testid={`enduser-modal-contact-${c.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span className="t-strong" style={{ fontSize: 13 }}>{c.name || "(no name)"}</span>
                {c.is_primary && <span className="badge b-gold" style={{ fontSize: 10 }}><span className="bd" />Primary</span>}
              </div>
              <div className="mono t-mut" style={{ fontSize: 11 }}>
                {c.email || "no email"} · {c.phone || "no phone"}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="form-grid">
        <div className="fld">
          <label>Name</label>
          <input className="input" value={name} onChange={(e) => { touch(); setName(e.target.value); setContactId(null); }}
            placeholder="End-user contact name" data-testid="enduser-modal-name" />
        </div>
        <div className="fld">
          <label>Email</label>
          <input className="input mono" value={email} onChange={(e) => { touch(); setEmail(e.target.value); }}
            placeholder="Notifications / certificate copy" data-testid="enduser-modal-email" />
        </div>
        <div className="fld">
          <label>Phone</label>
          <input className="input mono" value={phone} onChange={(e) => { touch(); setPhone(e.target.value); }}
            placeholder="WhatsApp / SMS" data-testid="enduser-modal-phone" />
        </div>
      </div>

      <div className="divider" style={{ margin: "16px 0 12px" }} />
      <div className="sec-h" style={{ marginTop: 0 }}>
        <h3 style={{ fontSize: 13 }}><MapPin size={13} style={{ verticalAlign: "-2px", color: "var(--gold-deep)" }} /> Certificate address</h3>
        <button className="btn ghost sm" type="button" onClick={fillFromCustomer}
          disabled={!customer} data-testid="enduser-modal-prefill-customer">
          ↺ Fill from customer record
        </button>
      </div>

      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, marginBottom: 10 }}>
        <input type="checkbox" checked={addrUnknown}
          onChange={(e) => { touch(); setAddrUnknown(e.target.checked); }}
          data-testid="enduser-modal-addr-unknown" />
        Address not supplied (record a reason)
      </label>

      {addrUnknown ? (
        <div className="form-grid">
          <div className="fld"><label>Company / name</label>
            <input className="input" value={company} onChange={(e) => { touch(); setCompany(e.target.value); }}
              placeholder="If known" data-testid="enduser-modal-company" /></div>
          <div className="fld" style={{ gridColumn: "span 2" }}><label>Reason address not supplied <span className="req">*</span></label>
            <input className="input" value={addrUnknownReason}
              onChange={(e) => { touch(); setAddrUnknownReason(e.target.value); }}
              placeholder="e.g. Customer to confirm delivery site"
              data-testid="enduser-modal-addr-reason" /></div>
        </div>
      ) : (
        <div className="form-grid">
          <div className="fld"><label>Company / name</label>
            <input className="input" value={company} onChange={(e) => { touch(); setCompany(e.target.value); }}
              placeholder="End-user company (cert name)" data-testid="enduser-modal-company" /></div>
          <div className="fld"><label>Address line 1</label>
            <input className="input" value={addr1} onChange={(e) => { touch(); setAddr1(e.target.value); }}
              data-testid="enduser-modal-addr1" /></div>
          <div className="fld"><label>Address line 2</label>
            <input className="input" value={addr2} onChange={(e) => { touch(); setAddr2(e.target.value); }} /></div>
          <div className="fld"><label>City</label>
            <input className="input" value={city} onChange={(e) => { touch(); setCity(e.target.value); }}
              data-testid="enduser-modal-city" /></div>
          <div className="fld"><label>Province</label>
            <input className="input" value={province} onChange={(e) => { touch(); setProvince(e.target.value); }} /></div>
          <div className="fld"><label>Country</label>
            <input className="input" value={country} onChange={(e) => { touch(); setCountry(e.target.value); }} /></div>
          <div className="fld"><label>Postal code</label>
            <input className="input mono" value={postal} onChange={(e) => { touch(); setPostal(e.target.value); }} /></div>
        </div>
      )}
    </Modal>
  );
}
